import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import { MIN_PREMIUM_QUALITY } from 'consts'
import { TAppDispatch, TRootState } from 'store/types'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { handleApiError } from 'store/utils'
import { getAvatarModels_API } from 'api'
import slice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { userSelectors } from 'store/features/user/selectors'
import {
  AVATAR_DEFAULT_QUALITY,
  AVATAR_PAID_DEFAULT_QUALITY
} from 'store/features/aiAvatar/consts'

const { actions } = slice

// The quality to default to once the model is known: paid users get the premium
// default (720p), free users the non-premium one (480p). Either is only used
// when the model actually offers it.
const preferredDefaultQuality = (isFreeUser: boolean): number =>
  isFreeUser ? AVATAR_DEFAULT_QUALITY : AVATAR_PAID_DEFAULT_QUALITY

// Pick a supported fallback quality: the lowest non-premium one for free users
// (so the default isn't a gated value they can't use), otherwise the lowest
// supported. `possibleQualities` is assumed non-empty.
const resolveSupportedQuality = (
  possibleQualities: number[],
  isFreeUser: boolean
): number => {
  const sorted = [...possibleQualities].sort((a, b) => a - b)
  const lowestNonPremium = sorted.find(q => q < MIN_PREMIUM_QUALITY)
  return isFreeUser && lowestNonPremium !== undefined
    ? lowestNonPremium
    : sorted[0]
}

// Fetches the avatar models catalog once and caches it in redux. Re-entrant
// calls are skipped while a request is in flight or after data has loaded, so
// re-opening the AI Avatar tab doesn't refetch. We keep only `id` and
// `possibleQualities` — all the quality picker needs.
const getAvatarModels =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const data = aiAvatarSelectors.avatarModelsData(getState())
    const isLoading = aiAvatarSelectors.isAvatarModelsLoading(getState())
    if (data?.length || isLoading) {
      return
    }

    dispatch(actions.setAvatarModelsLoading(true))
    try {
      const response = await getAvatarModels_API()
      const models = response.data.map(
        ({ id, possibleQualities, options }) => ({
          id,
          possibleQualities,
          options
        })
      )
      dispatch(actions.setAvatarModels(models))

      // Reconcile the seeded quality against the loaded model + the user's plan.
      // The params are seeded with AVATAR_DEFAULT_QUALITY (480p) before we know
      // either; once the model is known we:
      //  - bump a paid user from the seeded default to the premium default
      //    (720p) when the model offers it, and
      //  - snap any quality the model doesn't offer to a supported value (else
      //    the picker header renders blank, the cost estimate resolves to 0, and
      //    an unsupported quality would be sent to the backend).
      // A quality the user already picked manually (≠ the seeded default) is left
      // untouched as long as it's supported.
      dispatch(reconcileQuality())
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setAvatarModelsLoading(false))
    }
  }

// Reconciles the current quality against the loaded model + the user's plan.
// Safe to call any time: early-returns when the models catalog isn't loaded yet
// (empty `possibleQualities`), so the only effect is the paid bump / the snap of
// an unsupported quality once the catalog is known. Shared by `getAvatarModels`
// (first load) and `resetGenerationParams` (so a reset re-applies the plan-aware
// default instead of leaving the hard 480p seed in place).
const reconcileQuality =
  () => (dispatch: TAppDispatch, getState: () => TRootState) => {
    const possibleQualities =
      aiAvatarSelectors.avatarPossibleQualities(getState())
    if (!possibleQualities.length) {
      return
    }

    const currentQuality = aiAvatarSelectors.quality(getState())
    const isFreeUser = userSelectors.isFreeUser(getState())
    const preferred = preferredDefaultQuality(isFreeUser)
    const isUntouchedDefault = currentQuality === AVATAR_DEFAULT_QUALITY

    let nextQuality = currentQuality
    if (isUntouchedDefault && possibleQualities.includes(preferred)) {
      nextQuality = preferred
    } else if (!possibleQualities.includes(currentQuality)) {
      nextQuality = resolveSupportedQuality(possibleQualities, isFreeUser)
    }

    if (nextQuality !== currentQuality) {
      dispatch(actions.setQuality(nextQuality))
    }
  }

// Resetting the form seeds quality back to the hard 480p default, so re-run
// reconciliation to restore the plan-aware default (e.g. bump a paid user back
// to 720p when the loaded model offers it). This thunk overrides the raw slice
// action of the same name in `aiAvatarThunks` — the thunk groups are spread
// after `...actions`, so it wins — meaning both reset call sites (the sidebar
// double-click and the timeline deselect) pick it up without changes.
const resetGenerationParams = () => (dispatch: TAppDispatch) => {
  dispatch(actions.resetGenerationParams())
  dispatch(reconcileQuality())
}

export const avatarModelsThunks = {
  getAvatarModels,
  resetGenerationParams
}
